import { useEffect, useState } from 'react'
import { Activity, CheckCircle2, XCircle, RefreshCw } from 'lucide-react'
import { api } from '../../api/client'
import { PageHeader } from '../../components/PageHeader'
import { useI18n } from '../../i18n'

type HealthCheck = { service: string; status: string; message?: string }
type SystemHealth = { checks: HealthCheck[]; allOk: boolean; checkedAt: string }

export function SystemHealthPage() {
  const { t } = useI18n()
  const [data, setData] = useState<SystemHealth | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = () => {
    setLoading(true)
    api.get<SystemHealth>('/admin/system-health')
      .then(setData)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  return <>
    <PageHeader title="admin.systemHealth" action={
      <button onClick={load} disabled={loading} className="flex h-10 items-center gap-2 rounded-md border bg-card px-4 text-sm font-medium disabled:opacity-50">
        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />{t('admin.healthRunCheck')}
      </button>
    } />

    {error && <p className="mb-4 text-sm text-destructive">{error}</p>}

    {data && (
      <div className="space-y-5">
        {/* Status banner */}
        <div className={`panel p-5 flex items-center gap-4 ${data.allOk ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
          {data.allOk
            ? <CheckCircle2 size={28} className="text-emerald-600" />
            : <XCircle size={28} className="text-red-600" />
          }
          <div>
            <p className="font-semibold text-lg">{data.allOk ? t('admin.healthAllOk') : t('admin.healthHasErrors')}</p>
            <p className="text-sm text-muted-foreground">{t('admin.healthCheckedAt')}: {new Date(data.checkedAt).toLocaleString()}</p>
          </div>
        </div>

        {/* Service list */}
        <div className="panel overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30">
              <tr>
                <th className="px-5 py-3 text-left font-medium text-muted-foreground">{t('admin.healthService')}</th>
                <th className="px-5 py-3 text-left font-medium text-muted-foreground">{t('admin.healthStatus')}</th>
                <th className="px-5 py-3 text-left font-medium text-muted-foreground">{t('admin.adminNote')}</th>
              </tr>
            </thead>
            <tbody>
              {data.checks.map((check) => (
                <tr key={check.service} className="border-b last:border-0">
                  <td className="px-5 py-4 font-medium flex items-center gap-3">
                    <Activity size={16} className="text-muted-foreground" />
                    {check.service}
                  </td>
                  <td className="px-5 py-4">
                    {check.status === 'ok' ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                        <CheckCircle2 size={12} />{t('admin.healthOk')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        <XCircle size={12} />{t('admin.healthError')}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-muted-foreground text-xs">{check.message || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )}
  </>
}
